import React, { useState } from 'react';
import { AlertTriangle, ArrowLeft, ChevronDown, CloudOff, LogOut, Menu, MapPin, MoreVertical, Smartphone, Users, Wifi } from 'lucide-react';
import NotificationBell from '../Common/NotificationBell';
import MessageCenter from '../Common/MessageCenter';
import { TownSelectionModal } from '../Common/TownSelectionModal';
import { Town } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useBrand } from '../../contexts/BrandContext';
import { useGlobalToast } from '../../contexts/ToastContext';
import { ThemeToggle } from '../Common/ThemeToggle';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { usePWA } from '../../contexts/PWAContext';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { usePermissions } from '../../hooks/usePermissions';
import { PermissionGate } from '../Common/PermissionGate';
import { PERMISSIONS } from '../../utils/permissions';
import { authApi } from '../../services/api';

interface HeaderProps {
  towns: Town[];
  selectedTownId: string;
  onTownChange: (townId: string) => void;
  onMenuToggle?: () => void;
  onSwitchToMemberPortal?: (townId: string) => void;
  onNavigateToMessages?: (contactId?: string) => void;
}

export const Header: React.FC<HeaderProps> = ({ towns, selectedTownId, onTownChange, onMenuToggle, onSwitchToMemberPortal, onNavigateToMessages }) => {
  const { user, logout } = useAuth();
  const { brand } = useBrand();
  const { success, error } = useGlobalToast();
  const { isSuperAdmin, isFinanceAdmin, isTownAdmin, hasPermission } = usePermissions();
  const { isOnline, networkStatus } = usePWA();
  const [showIndicator] = useLocalStorage<boolean>('pwa.networkStatus.enabled', true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [townSelectionModalOpen, setTownSelectionModalOpen] = useState(false);
  const [switchingToMember, setSwitchingToMember] = useState(false);
  const selectedTown = selectedTownId === 'all' || selectedTownId === 'no-town-assigned' ? null : towns.find(t => t.id === selectedTownId);
  
  const getAdminTypeLabel = () => {
    if (user?.isAdminMemberView) return `${user.originalAdminRole?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} (Member View)`;
    if (isSuperAdmin()) return 'Super Administrator';
    if (isFinanceAdmin()) return 'Finance Administrator';
    if (isTownAdmin()) return 'Town Administrator';
    return 'Member Administrator';
  };

  const getStatus = () => {
    if (!isOnline) return { icon: CloudOff, color: 'red' as const, text: 'Offline' };
    if (networkStatus === 'slow') return { icon: AlertTriangle, color: 'yellow' as const, text: 'Slow' };
    return { icon: Wifi, color: 'green' as const, text: 'Online' };
  };

  const NetworkStatusBadge: React.FC = () => {
    if (!showIndicator) return null;

    const status = getStatus();
    const Icon = status.icon;

    return (
      <div
        data-testid="network-status-indicator"
        className="transition-all duration-200"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        title={status.text}
      >
        <Button
          variant="outline"
          size="icon"
          className={`border-${status.color === 'yellow' ? 'amber' : status.color}-200 bg-${status.color === 'yellow' ? 'amber' : status.color}-50/50 text-${status.color === 'yellow' ? 'amber' : status.color}-700 hover:bg-${status.color === 'yellow' ? 'amber' : status.color}-100 shadow-sm backdrop-blur-sm animate-pulse`}
          style={{ animationDuration: '2s' }}
        >
          <Icon className={`h-4 w-4 shrink-0 ${status.color === 'green' ? 'text-green-500' : status.color === 'yellow' ? 'text-amber-500' : 'text-red-500'}`} />
        </Button>
      </div>
    );
  };

  const handleSwitchToMemberPortal = async (townId: string) => {
    setSwitchingToMember(true);
    try {
      const currentToken = localStorage.getItem('auth_token');
      if (!currentToken) {
        error('Authentication required', 'Please log in again.');
        return;
      }

      const response = await authApi.switchToMemberPortal(currentToken, townId);
      if (response.success && response.data) {
        // Store original token for restoration
        localStorage.setItem('original_auth_token', currentToken);
        // Store the new member portal token
        localStorage.setItem('auth_token', String(response.data.token));
        localStorage.setItem('user_data', JSON.stringify(response.data.user));
        
        success('Switched to Member Portal', 'You are now viewing the member portal.');
        
        // Trigger portal switch
        if (onSwitchToMemberPortal) {
          onSwitchToMemberPortal(townId);
        } else {
          // Fallback: reload the page to switch context
          window.location.reload();
        }
      } else {
        error('Switch Failed', response.error || 'Failed to switch to member portal.');
      }
    } catch (err) {
      console.error('Failed to switch to member portal:', err);
      error('Switch Failed', 'Network error occurred while switching to member portal.');
    } finally {
      setSwitchingToMember(false);
      setTownSelectionModalOpen(false);
    }
  };

  const handleBackToAdmin = async () => {
    try {
      // Get the original token from localStorage backup
      const originalToken = localStorage.getItem('original_auth_token');
      
      if (originalToken) {
        // Restore original token
        localStorage.setItem('auth_token', originalToken);
        localStorage.removeItem('original_auth_token');
        localStorage.removeItem('user_data');
        success(`Returned to ${user?.originalUserRole === 'developer' ? 'Developer' : 'Admin'} Portal`);
        window.location.reload();
      } else {
        // Fallback: clear everything and force re-login
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        success('Session ended', 'Please log in again.');
        window.location.reload();
      }
    } catch (err) {
      console.error('Failed to return to original portal:', err);
      error('Return Failed', 'Please refresh the page to return.');
    }
  };

  return (
    <header className="bg-white dark:bg-dark-bg-primary shadow-sm border-b border-gray-200 dark:border-dark-border-primary px-4 py-3 sticky top-0 z-30">
      <div className="flex items-center justify-between">
        {/* Click outside to close mobile menu */}
        {mobileMenuOpen && (
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setMobileMenuOpen(false)}
          />
        )}
        <div className="flex items-center space-x-2 md:space-x-4">
          {/* Mobile menu button */}
          <button
            onClick={onMenuToggle}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-bg-secondary transition-colors"
          >
            <Menu className="h-5 w-5 text-gray-600 dark:text-dark-text-secondary" />
          </button>
          <div className="flex items-center space-x-2">
            <img 
              src={brand.brandLogo} 
              alt={brand.brandName}
              className="h-6 w-6 md:h-8 md:w-8 object-contain"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                if (nextElement) {
                  nextElement.style.display = 'block';
                }
              }}
            />
            <div 
              className="bg-blue-600 p-1 md:p-2 rounded-lg" 
              style={{ display: 'none', backgroundColor: brand.brandColors.primary }}
            >
              <Users className="h-4 w-4 md:h-5 md:w-5 text-white" />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-sm md:text-lg font-bold text-gray-900 dark:text-dark-text-primary">
                {brand.brandName.toUpperCase()}
              </h1>
              <p className="text-xs text-gray-500 dark:text-dark-text-secondary hidden md:block">
                {brand.brandTagline}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2 gap-2">
          {/* Desktop town selector */}
          <PermissionGate permissions={[PERMISSIONS.MANAGE_USERS, PERMISSIONS.APPROVE_FINANCES]}>
            <div className="relative hidden lg:block">
              {selectedTownId === 'no-town-assigned' ? (
                <div className="bg-gray-100 dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border-primary rounded-lg px-3 py-2 text-sm font-medium text-gray-500 dark:text-dark-text-secondary">
                  No Town Assigned
                </div>
              ) : (
                <>
                  <select
                    value={selectedTownId}
                    onChange={(e) => onTownChange(e.target.value)}
                    className="appearance-none bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border-primary rounded-lg px-3 py-2 pr-8 text-sm font-medium text-gray-700 dark:text-dark-text-primary hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  >
                    <option value="all">All Towns</option>
                    {towns.map((town) => (
                      <option key={town.id} value={town.id}>
                        {town.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-dark-text-secondary pointer-events-none" />
                </>
              )}
            </div>
          </PermissionGate>

          {/* Notifications and Messages */}
          <div className="flex items-center space-x-2 shrink-0">
            <MessageCenter onNavigateToMessages={onNavigateToMessages} />
            <NotificationBell />
          </div>

          {/* Desktop theme toggle */}
          <div className="hidden lg:block">
            <ThemeToggle />
          </div>
          
          <NetworkStatusBadge />
          
          {/* Desktop location info */}
          <div className="hidden xl:flex items-center space-x-2 text-sm text-gray-600 dark:text-dark-text-secondary">
            <MapPin className="h-4 w-4" />
            <span>
              {user?.role === 'admin' 
                ? (selectedTown ? selectedTown.location : 'All Regions')
                : towns.find(t => t.id === user?.townId)?.location || 'Unknown Location'
              }
            </span>
          </div>

          {/* Desktop user info */}
          <div className="hidden lg:flex items-center space-x-3">
            <div className="text-right">
              <p className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                {user?.role === 'admin' ? getAdminTypeLabel() : user?.name}
              </p>
              <p className="text-xs text-gray-500 dark:text-dark-text-secondary">
                {user?.username}
              </p>
            </div>
            
            {/* Portal Navigation Buttons */}
            {user?.isAdminMemberView ? (
              <button
                onClick={handleBackToAdmin}
                className="text-sm bg-blue-600 hover:bg-blue-700 text-white font-medium px-3 py-1 rounded-lg transition-colors flex items-center space-x-1"
              >
                <ArrowLeft className="h-3 w-3" />
                <span>Back to {user?.originalUserRole === 'developer' ? 'Developer' : 'Admin'}</span>
              </button>
            ) : user?.isDevAdminView ? (
              <button
                onClick={handleBackToAdmin}
                className="text-sm bg-purple-600 hover:bg-purple-700 text-white font-medium px-3 py-1 rounded-lg transition-colors flex items-center space-x-1"
              >
                <ArrowLeft className="h-3 w-3" />
                <span>Back to Developer</span>
              </button>
            ) : (
              user?.role !== 'member' && (
                <button
                  onClick={() => setTownSelectionModalOpen(true)}
                  className="text-sm bg-green-600 hover:bg-green-700 text-white font-medium px-3 py-1 rounded-lg transition-colors flex items-center space-x-1"
                >
                  <Smartphone className="h-3 w-3" />
                  <span>Member Portal</span>
                </button>
              )
            )}
            
          </div>

          {/* Logout button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={logout}
            title="Logout"
            className="text-red-600 hover:text-red-800 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <LogOut className="h-4 w-4" />
          </Button>
            
          {/* Mobile menu button */}
          <div className="relative lg:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-bg-secondary transition-colors"
            >
              <MoreVertical className="h-5 w-5 text-gray-600 dark:text-dark-text-secondary" />
            </button>
              
            {mobileMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-dark-bg-secondary rounded-lg shadow-lg border border-gray-200 dark:border-dark-border-primary z-50">
                <div className="p-3">
                  {/* User info */}
                  <div className="px-3 py-2 border-b border-gray-200 dark:border-dark-border-primary mb-2">
                    <p className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                      {user?.role === 'admin' ? getAdminTypeLabel() : user?.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-dark-text-secondary">
                      {user?.username}
                    </p>
                  </div>
                  
                  {/* Town selector for mobile */}
                  {(hasPermission(PERMISSIONS.MANAGE_USERS) || hasPermission(PERMISSIONS.APPROVE_FINANCES)) && (
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-gray-500 dark:text-dark-text-secondary mb-1">Select Town</label>
                      {selectedTownId === 'no-town-assigned' ? (
                        <div className="w-full bg-gray-100 dark:bg-dark-bg-primary border border-gray-300 dark:border-dark-border-primary rounded-lg px-3 py-2 text-sm text-gray-500 dark:text-dark-text-secondary">
                          No Town Assigned
                        </div>
                      ) : (
                        <select
                          value={selectedTownId}
                          onChange={(e) => {
                            onTownChange(e.target.value);
                            setMobileMenuOpen(false);
                          }}
                          className="w-full bg-white dark:bg-dark-bg-primary border border-gray-300 dark:border-dark-border-primary rounded-lg px-3 py-2 text-sm"
                        >
                          <option value="all">All Towns</option>
                          {towns.map((town) => (
                            <option key={town.id} value={town.id}>
                              {town.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                  
                  {/* Theme toggle */}
                  <div className="mb-3">
                    <ThemeToggle />
                  </div>
                  
                  {/* Portal Navigation Buttons (Mobile) */}
                  {user?.isAdminMemberView ? (
                    <button
                      onClick={() => {
                        handleBackToAdmin();
                        setMobileMenuOpen(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors flex items-center space-x-2 mb-2"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      <span>Back to {user?.originalUserRole === 'developer' ? 'Developer' : 'Admin'} Portal</span>
                    </button>
                  ) : user?.isDevAdminView ? (
                    <button
                      onClick={() => {
                        handleBackToAdmin();
                        setMobileMenuOpen(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm bg-purple-600 text-white hover:bg-purple-700 rounded-lg transition-colors flex items-center space-x-2 mb-2"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      <span>Back to Developer Portal</span>
                    </button>
                  ) : (
                    user?.role !== 'member' && (
                      <button
                        onClick={() => {
                          setTownSelectionModalOpen(true);
                          setMobileMenuOpen(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm bg-green-600 text-white hover:bg-green-700 rounded-lg transition-colors flex items-center space-x-2 mb-2"
                      >
                        <Smartphone className="h-4 w-4" />
                        <span>Switch to Member Portal</span>
                      </button>
                    )
                  )}
                  
                  {/* Logout button */}
                  <button
                    onClick={() => {
                      logout();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  >
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Town Selection Modal */}
      <TownSelectionModal
        isOpen={townSelectionModalOpen}
        onClose={() => setTownSelectionModalOpen(false)}
        towns={towns}
        onSelectTown={handleSwitchToMemberPortal}
        loading={switchingToMember}
      />
    </header>
  );
};